import Button from "@components/_Layout/Controls/Button";
import classNames from "classnames";
import PropTypes from "prop-types";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { Disclosure } from "@headlessui/react";
import { faBars, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NavLink } from "react-router-dom";
import { OverridableComponent } from "@mui/material/OverridableComponent";
import { sortBy } from "@utils/arrays";
import { SvgIconTypeMap } from "@mui/material/SvgIcon";
import { useAuth, useSession } from "@hooks";
import { redirectTo } from "@utils/tools";

export type TNavigationAreaPath = {
  name: string;
  description?: string;
  to: string;
  order: number;
  muiIcon?: OverridableComponent<SvgIconTypeMap> & { muiName: string };
};

type NavigationAreaProps = {
  home: string;
  paths: TNavigationAreaPath[];
};

const languagesVarsCodes = [
  "LANG_neobooking_disconnection_in_progress", //Déconnexion en cours...
  "LANG_neobooking_atm_disconnected", //Vous êtes maintenant déconnectéLANG_neobooking_open_menu
  "LANG_neobooking_open_menu", //Ouvrir le menu
  "LANG_neobooking_atm_disconnected", //Vous êtes maintenant déconnectéLANG_neobooking_open_menu
  "LANG_neobooking_atm_disconnected", //Vous êtes maintenant déconnectéLANG_neobooking_open_menu
];

const NavigationArea: React.FC<NavigationAreaProps> = ({ home, paths }) => {
  /* Hooks */
  const { signout } = useAuth();
  const { getLanguageVarValue } = useSession();

  /* State */
  const [isLogout, setIsLogout] = useState<boolean>(false);

  const handleLogout = () => {
    setIsLogout(true);
    const toastLogout = toast.loading(
      getLanguageVarValue("LANG_neobooking_disconnection_in_progress")
    );
    signout((err) => {
      if (!err) {
        toast.success(getLanguageVarValue("LANG_neobooking_atm_disconnected"), {
          id: toastLogout,
        });
        redirectTo(window.location.origin);
      } else
        toast.error(err.message, {
          id: toastLogout,
        });
      setIsLogout(false);
    });
  };

  return (
    <>
      <Disclosure
        as="div"
        className="tw-bg-gray-50 tw-h-18 tw-items-center"
        style={{ zIndex: "999" }}
      >
        {({ open }) => (
          <>
            <div className="tw-container tw-mx-auto">
              <div className="tw-relative tw-flex tw-items-center tw-justify-between tw-h-16">
                <div className="tw-absolute tw-inset-y-0 left-0 tw-flex tw-items-center sm:tw-hidden">
                  {/* Mobile menu button*/}
                  <Disclosure.Button className="tw-inline-flex tw-items-center tw-justify-center tw-p-2 tw-rounded-md tw-text-dark-light hover:tw-text-dark-dark focus:tw-outline-none">
                    <span className="tw-sr-only">
                      {getLanguageVarValue("LANG_neobooking_open_menu")}
                    </span>
                    {open ? (
                      <FontAwesomeIcon
                        width="20"
                        height="20"
                        className="tw-block tw-h-6 tw-w-6 hover:tw-text-primary-500 tw-transition-colors tw-duration-200 tw-ease-in-out"
                        aria-hidden="true"
                        icon={faTimes}
                      ></FontAwesomeIcon>
                    ) : (
                      <FontAwesomeIcon
                        width="20"
                        height="20"
                        className="tw-block tw-h-6 tw-w-6 hover:tw-text-primary-500 tw-transition-colors tw-duration-200 tw-ease-in-out"
                        aria-hidden="true"
                        icon={faBars}
                      ></FontAwesomeIcon>
                    )}
                  </Disclosure.Button>
                </div>
                <div className="tw-flex-1 tw-flex tw-items-center tw-justify-center sm:tw-items-stretch sm:tw-justify-start">
                  <div className="tw-flex-shrink-0 tw-flex tw-items-center">
                    <div className="tw-hidden sm:tw-block">
                      <ul className="tw-flex tw-items-center tw-space-x-6">
                        <li className="tw-flex tw-flex-row tw-items-center tw-gap-1">
                          <NavLink
                            to={home}
                            end
                            className={(navData) =>
                              classNames(
                                navData.isActive ? "tw-text-primary-500" : "",
                                "tw-text-sm tw-rounded tw-py-2 tw-font-medium focus:tw-outline-none tw-cursor-pointer hover:tw-text-primary-500 tw-transition-colors tw-duration-200 tw-ease-in-out"
                              )
                            }
                          >
                            Accueil
                          </NavLink>
                        </li>
                        {sortBy(paths, (x) => x.order).map((item) => {
                          return (
                            <li
                              className="tw-flex tw-flex-row tw-items-center tw-gap-1"
                              key={`partner_menu_${item.name}`}
                            >
                              <NavLink
                                to={item.to}
                                end
                                className={(navData) =>
                                  classNames(
                                    navData.isActive
                                      ? "tw-text-primary-500"
                                      : "",
                                    "tw-text-sm tw-rounded tw-py-2 tw-font-medium focus:tw-outline-none tw-cursor-pointer hover:tw-text-primary-500 tw-transition-colors tw-duration-200 tw-ease-in-out"
                                  )
                                }
                              >
                                {item.name}
                              </NavLink>
                            </li>
                          );
                        })}
                        <li className="tw-flex tw-flex-row tw-items-center tw-gap-1">
                          <Button
                            type="button"
                            loadingChildren={
                              <>
                                {getLanguageVarValue(
                                  "LANG_neobooking_disconnection_in_progress"
                                )}
                              </>
                            }
                            isLoading={isLogout}
                            onClick={handleLogout}
                            className="tw-text-sm tw-rounded tw-font-medium focus:tw-outline-none tw-cursor-pointer hover:tw-text-primary-500 tw-transition-colors tw-duration-200 tw-ease-in-out"
                          >
                            {getLanguageVarValue("LANG_neobooking_logout")}
                          </Button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <Disclosure.Panel className="sm:tw-hidden">
              <div className="tw-px-2 tw-pt-2 tw-pb-3 tw-flex tw-flex-col tw-gap-3">
                <NavLink
                  to={home}
                  end
                  className={(navData) =>
                    classNames(
                      navData.isActive ? "tw-text-primary-500" : "",
                      "tw-text-sm tw-px-3 tw-font-medium focus:tw-outline-none tw-cursor-pointer hover:tw-text-primary-500 tw-transition-colors tw-duration-200 tw-ease-in-out"
                    )
                  }
                >
                  Accueil
                </NavLink>
                {paths.map((item) => {
                  return (
                    <NavLink
                      key={`nav_partner_menu_${item.name}`}
                      to={item.to}
                      end
                      className={(navData) =>
                        classNames(
                          navData.isActive ? "tw-text-primary-500" : "",
                          "tw-text-sm tw-px-3 tw-font-medium focus:tw-outline-none tw-cursor-pointer hover:tw-text-primary-500 tw-transition-colors tw-duration-200 tw-ease-in-out"
                        )
                      }
                    >
                      {item.name}
                    </NavLink>
                  );
                })}
                <Button
                  type="button"
                  loadingChildren={
                    <>
                      {getLanguageVarValue(
                        "LANG_neobooking_disconnection_in_progress"
                      )}
                    </>
                  }
                  isLoading={isLogout}
                  onClick={handleLogout}
                  className="tw-flex tw-text-sm tw-px-3 tw-font-medium focus:tw-outline-none tw-cursor-pointer hover:tw-text-primary-500 tw-transition-colors tw-duration-200 tw-ease-in-out"
                >
                  {getLanguageVarValue("LANG_neobooking_logout")}
                </Button>
              </div>
            </Disclosure.Panel>
          </>
        )}
      </Disclosure>
    </>
  );
};

NavigationArea.propTypes = {
  //
};

export default NavigationArea;
